import { useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import Value, { getValue } from "aldoo-ra/Value"
import TextBundle from "aldoo-ra/CMS/text-bundle"

let instance = null

const STEP_DATA_KEY = 'purchase-flow-step-data'

// Steps that never appear in the progress header (the cart is a separate
// modal, success replaces the checkout, payment-reuse is an alias of payment)
const PROGRESS_HIDDEN = new Set(['cart', 'success', 'payment-reuse'])

/**
 * PubSub topics a step (or anything else) can publish:
 * - PURCHASE_FLOW_UPDATE_BUTTON { stepId, canContinue, loading, continueText, hideButtons, onContinue, onBack }
 * - PURCHASE_FLOW_NEXT { stepData }   store the data of the current step and advance
 * - PURCHASE_FLOW_BACK                go back (or to the cart from the first step)
 * - PURCHASE_FLOW_GOTO { stepId }     jump to a step by id (e.g. an optional step)
 * - PURCHASE_FLOW_SET_DATA { stepId, data }  store data for any step without navigating
 * - PURCHASE_FLOW_REFRESH             re-evaluate the current step's skip condition
 */
const usePurchaseFlowController = ({ store: storeProp }) => {
  // The store interface object is rebuilt on every render of AldooStore while
  // the PubSub handlers below are bound once per flow: read the store through
  // a ref so conditions always see the current cart / auth / paymentInfo.
  const storeRef = useRef(storeProp)
  storeRef.current = storeProp
  const store = storeRef.current
  const { purchaseFlow } = store
  const texts = TextBundle({ id: "aldoo-store" })

  // Flow state using Value with unique keys
  const [currentStep, setCurrentStep] = Value('purchase-flow-current-step', 'email')
  const [flowInitialized, setFlowInitialized] = Value('purchase-flow-initialized', false)

  // Step data map - centralized storage for all step data
  const [stepDataMap, setStepDataMap] = Value(STEP_DATA_KEY, {})

  // Button state for current step
  const [canContinue, setCanContinue] = Value('purchase-flow-can-continue', false)
  const [loading, setLoading] = Value('purchase-flow-loading', false)
  const [continueText, setContinueText] = Value('purchase-flow-continue-text', texts?.continue || "Continue")
  const [hideButtons, setHideButtons] = Value('purchase-flow-hide-buttons', false)

  // Refs for callbacks
  const onContinueRef = useRef(null)
  const onBackRef = useRef(null)

  // Helper functions for step data. The PubSub handlers below are bound once
  // per flow, so they read the map through getValue (always current) instead
  // of the render-time snapshot.
  const readStepData = () => getValue(STEP_DATA_KEY) || {}
  const getData = (stepId) => readStepData()[stepId] || null
  const setData = (stepId, data) => {
    setStepDataMap({
      ...readStepData(),
      [stepId]: data
    })
  }

  // Get flow context (always from the current store, see storeRef)
  const getFlowContext = () => {
    const current = storeRef.current
    const paymentReuseData = getData('payment-reuse')
    return {
      auth: current.auth,
      tempAuthForPurchase: current.tempAuthForPurchase,
      purchaseUserEmail: current.purchaseUserEmail,
      paymentInfo: current.paymentInfo,
      invoiceData: getData('invoice') || current.preloadedInvoiceData,
      useNewPaymentMethod: paymentReuseData?.useNewPaymentMethod || false,
      // Available to project steps / conditions
      cart: current.cart || [],
      stepData: readStepData(),
      getData,
      config: current.config,
      store: current,
    }
  }

  const stepButtonText = (step) => {
    const text = step?.buttonText
    if (typeof text === 'function') return text(getFlowContext()) || texts?.continue || "Continue"
    return text || texts?.continue || "Continue"
  }

  // Initialize flow when checkout opens
  useEffect(() => {
    if (!purchaseFlow || !store.checkoutVisible) return

    if (!flowInitialized) {
      const context = getFlowContext()

      const step = purchaseFlow.initialize(context)
      const visibleSteps = purchaseFlow.getVisibleSteps(context)

      if (step) {
        setCurrentStep(step.id)
        setContinueText(stepButtonText(step))
        setFlowInitialized(true)
      }
    }
  }, [purchaseFlow, store.checkoutVisible, flowInitialized])

  // Re-evaluate steps when payment info loads
  // This handles two cases:
  // 1. Invoice step when billing details load after checkout starts
  // 2. Payment step when saved payment method is discovered
  useEffect(() => {
    if (!purchaseFlow || !flowInitialized || !store.paymentInfo) return

    const context = getFlowContext()
    const currentStepDef = purchaseFlow.getCurrentStep()

    // Re-evaluate if we're on invoice step and it should now be skipped
    if (currentStepDef && currentStepDef.id === 'invoice' && purchaseFlow.shouldSkipStep(currentStepDef, context)) {
      const nextStep = purchaseFlow.goToNext(context)
      if (nextStep) {
        setCurrentStep(nextStep.id)
        setContinueText(stepButtonText(nextStep))
      }
      return
    }

    // Re-evaluate if we're on payment step and user has saved payment method
    // In this case, we should go to payment-reuse instead
    if (currentStepDef && currentStepDef.id === 'payment' && store.paymentInfo.savedPaymentMethod) {
      // Check if current payment step should be skipped with the new context
      if (purchaseFlow.shouldSkipStep(currentStepDef, context)) {
        // Move to the payment-reuse step of this flow (position depends on the flow)
        const reuseIndex = purchaseFlow.getStepIndex('payment-reuse')
        if (reuseIndex !== -1) {
          purchaseFlow.currentStepIndex = reuseIndex
          const paymentReuseStep = purchaseFlow.getCurrentStep()
          setCurrentStep(paymentReuseStep.id)
          setContinueText(stepButtonText(paymentReuseStep))
        }
      }
    }
  }, [store.paymentInfo, flowInitialized, purchaseFlow])

  // Reset when checkout closes
  useEffect(() => {
    if (!store.checkoutVisible) {
      setFlowInitialized(false)
      setCurrentStep('email')
      setStepDataMap({})
      setCanContinue(false)
      setLoading(false)
      // Reset preloaded invoice data for next checkout
      if (store.setPreloadedInvoiceData) {
        store.setPreloadedInvoiceData(null)
      }
    }
  }, [store.checkoutVisible])

  // Subscribe to PubSub events
  useEffect(() => {
    const enterStep = (step) => {
      setCurrentStep(step.id)
      // Reset button state for new step, using step's buttonText if defined
      setCanContinue(false)
      setLoading(false)
      setContinueText(stepButtonText(step))
      onContinueRef.current = null
      onBackRef.current = null
    }

    // Step wants to update button state
    const updateButtonToken = PubSub.subscribe("PURCHASE_FLOW_UPDATE_BUTTON", (msg, data) => {
      const { stepId, canContinue: can, loading: isLoading, continueText: text, hideButtons: hide, onContinue, onBack } = data

      // Get the actual current step from the flow object (not React state which may be stale)
      const actualCurrentStep = purchaseFlow?.getCurrentStep()?.id

      // Only accept updates from the actual current step (from flow object, not React state)
      if (stepId && actualCurrentStep && stepId !== actualCurrentStep) {
        return
      }

      if (can !== undefined) setCanContinue(can)
      if (isLoading !== undefined) setLoading(isLoading)
      if (text) setContinueText(text)
      if (hide !== undefined) setHideButtons(hide)
      if (onContinue) {
        onContinueRef.current = onContinue
      }
      if (onBack) onBackRef.current = onBack
    })

    // Step completed, move to next
    const nextToken = PubSub.subscribe("PURCHASE_FLOW_NEXT", (msg, data) => {
      const { stepData } = data || {}
      const currentStepId = purchaseFlow?.getCurrentStep()?.id

      // Store step data for current step
      if (stepData && currentStepId) {
        setData(currentStepId, stepData)
        // Also store in store for legacy compatibility
        if (currentStepId === 'invoice') {
          storeRef.current.invoiceData = stepData
        }
      }

      // Build context with the new step data included
      // Important: We need to merge the incoming stepData into the context
      // BEFORE evaluating skip conditions, otherwise the data won't be available
      const baseContext = getFlowContext()
      const context = stepData?.useNewPaymentMethod !== undefined
        ? { ...baseContext, useNewPaymentMethod: stepData.useNewPaymentMethod }
        : baseContext

      const nextStep = purchaseFlow.goToNext(context)

      if (nextStep) enterStep(nextStep)
    })

    // Jump straight to a step by id, bypassing the linear order.
    // Used by optional steps that are opened on demand (e.g. the invoice form
    // reached from the "Need an invoice?" link on the payment step).
    const gotoToken = PubSub.subscribe("PURCHASE_FLOW_GOTO", (msg, data) => {
      const { stepId } = data || {}
      if (!stepId) return

      const step = purchaseFlow.goToStep(stepId, getFlowContext())
      if (!step) return

      enterStep(step)
    })

    // Store data for any step (e.g. a dialog updating a later step's data)
    const setDataToken = PubSub.subscribe("PURCHASE_FLOW_SET_DATA", (msg, data) => {
      const { stepId, data: stepData } = data || {}
      if (!stepId) return
      setData(stepId, stepData)
    })

    // Re-evaluate the current step: skip forward when its condition no longer holds
    const refreshToken = PubSub.subscribe("PURCHASE_FLOW_REFRESH", () => {
      const context = getFlowContext()
      const current = purchaseFlow?.getCurrentStep()
      if (!current || !purchaseFlow.shouldSkipStep(current, context)) return
      const nextStep = purchaseFlow.goToNext(context)
      if (nextStep) enterStep(nextStep)
    })

    // Step wants to go back
    const backToken = PubSub.subscribe("PURCHASE_FLOW_BACK", () => {
      const context = getFlowContext()
      const prevStep = purchaseFlow.goBack(context)
      if (prevStep) {
        // If going back to payment-reuse, clear the useNewPaymentMethod flag
        if (prevStep.id === 'payment-reuse') {
          setData('payment-reuse', null)
        }
        enterStep(prevStep)
      } else {
        // No previous step, show cart (user may want to add promo code)
        storeRef.current.hideCheckout()
        storeRef.current.showCart()
      }
    })

    return () => {
      PubSub.unsubscribe(updateButtonToken)
      PubSub.unsubscribe(nextToken)
      PubSub.unsubscribe(gotoToken)
      PubSub.unsubscribe(setDataToken)
      PubSub.unsubscribe(refreshToken)
      PubSub.unsubscribe(backToken)
    }
  }, [purchaseFlow]) // Don't depend on currentStep - we get it from flow object

  // Handle continue button click
  const handleContinue = async () => {
    if (onContinueRef.current) {
      await onContinueRef.current()
    }
  }

  // Handle back button click
  const handleBack = () => {
    if (onBackRef.current) {
      onBackRef.current()
    } else {
      PubSub.publish("PURCHASE_FLOW_BACK")
    }
  }

  // Get all flow steps for progress bar
  const getFlowSteps = () => {
    if (!purchaseFlow) return []
    const visitedSteps = purchaseFlow.getVisitedSteps()
    return visitedSteps.filter(s => !PROGRESS_HIDDEN.has(s.id) && !s.hideInProgress)
  }

  // Helper to check if a step is currently active
  const isStepActive = (stepId) => currentStep === stepId

  // Definition of the active step (null before the flow is initialized)
  const currentStepDef = purchaseFlow?.getStepById?.(currentStep) || null

  return {
    currentStep,
    currentStepDef,
    canContinue,
    loading,
    continueText,
    hideButtons,
    handleContinue,
    handleBack,
    getData,
    setData,
    stepData: stepDataMap,
    getFlowContext,
    getFlowSteps,
    isStepActive,
  }
}

// Singleton pattern
export default function PurchaseFlowController({ store, children }) {
  const controller = usePurchaseFlowController({ store })

  // Store instance for external access if needed
  useEffect(() => {
    instance = controller
  }, [controller])

  // Provide controller data to children via render props
  return children(controller)
}

// Export instance getter for external access
export const getPurchaseFlowController = () => instance
